import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IAttrValue } from "@multiweb/types/contracts";
import { CancellationToken } from "@multiweb/types/multiweb";
import { CultureInfo } from "Global/Dom/CultureInfo";
import { RequestContext } from "OW.Remoting/Remoting/RequestContext";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import { DigitalSignatureState } from "../../../../Global/Base/Crypto/ISignatureInfo";
import { ISignatureProviderProfile } from "../../../../Global/Base/Crypto/ISignatureProvider";
import { Event as GlobalEvent } from "../../../../Global/Base/Events";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { IValidatable, IValidationResult } from "../../../UI.BaseControls/ControlInterfaces";
import { GenericControl } from "../../../UI.BaseControls/GenericControl";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Avatar } from "../../../UI/UI/Controls/Avatar";
import { IHasFrameContext } from "../../../UI/UI/Controls/ControlInterfaces";
import { FileEditProgressStatus, FileEditStatus } from "../../../UI/UI/FileMenu";
import { FileEditCore, IFileEdit } from "../../../UI/UI/Helpers/FileEditCore";
import { FrameContext } from "../../../UI/UI/UIManagerInterfaces";
import { ICommentRowData } from "../../DataSource/CommentaryFrameDataSource";
import { FileEditElement } from "../../FileEditElement";
import { IDataGrid } from "../../IDataGrid";
import { Column } from "../Columns/Column";
import { IDataRow, IDataRowElement } from "./IDataRow";
import { TableCell } from "./TableCell";
import { CollapseState } from "./TreeViewDataRow";

const logger = LazyLoadingSectionLogger.create(LoggingSection.commentaryFrame);

export class CommentaryFrameDataRow extends HTMLElement implements IDataRowElement, IHasFrameContext {
    public readonly replyButtonClicked: GlobalEvent<CommentaryFrameDataRow, void> = new GlobalEvent<CommentaryFrameDataRow, void>();
    public readonly cellValidityChanged: GlobalEvent<IValidatable, IValidationResult> = new GlobalEvent<IValidatable, IValidationResult>();
    public readonly selectionStateChanged: GlobalEvent<IDataRow, boolean> = new GlobalEvent<IDataRow, boolean>();

    private _isAttached: boolean;
    private _isSelected: boolean;
    private _state: CollapseState;
    private _rowHeaderContainer: HTMLDivElement;
    private _rowCommentContainer: HTMLDivElement;
    private _rowAttachmentContainer: HTMLDivElement;
    private _rowFooterContainer: HTMLDivElement;
    private _rowFooterReply: HTMLButtonElement;
    private _childrenContainer: HTMLUListElement;
    private _archiveDocumentId: string;
    private _avatar: Avatar;
    private _file: File;
    private _signatureProviderProfile: ISignatureProviderProfile | null | undefined = undefined;
    private _digitalSignatureState: DigitalSignatureState;
    protected _fileEditElement: FileEditElement;
    private set _setSignatureProviderProfile(val: ISignatureProviderProfile | null | undefined) {
        this._signatureProviderProfile = val;
    }
    protected _isSigningInProgress: boolean = false;
    private set _signingInProgress(val: boolean) {
        this._isSigningInProgress = val;
    }
    private _setStatus(status: FileEditStatus): void {
        this._fileEditElement.setStatus(status);
    }
    protected _dataCells: Array<TableCell> = [];
    private _eventListFnc: any = {};
    private _hasNewRights: boolean = false;
    private _dataKey: string;
    public childrenRows: CommentaryFrameDataRow[] = [];
    private readonly _cssClass = "commentary-frame-data-row";
    private readonly _cssClassHeader = this._cssClass + "-header";
    private readonly _cssClassMid = this._cssClass + "-mid";
    private readonly _cssClassFooter = this._cssClass + "-footer";
    private readonly _cssClassElementContainer = this._cssClass + "__element-container";
    private readonly _cssClassAttachmentsContainer = this._cssClass + "-attachments";
    private _hideRepliesButton: HTMLButtonElement;
    private _rootParentDataKey: string;
    private _parentDataKey: string;

    public get dataGrid(): IDataGrid<IDataRowElement> {
        return undefined;
    }

    public get isAttached(): boolean {
        return this._isAttached;
    }

    public get hasNewRights(): boolean {
        return this._hasNewRights;
    }
    public set hasNewRights(v: boolean) {
        this._hasNewRights = v;
    }

    public get dataCells(): readonly TableCell[] {
        return this._dataCells;
    }

    public set visible(v: boolean) {
        this.classList.toggle("Hidden", !v);
    }

    public get avatar(): Avatar {
        return this._avatar;
    }

    /**
    * ID řádku
    */
    public get dataKey(): string {
        return this._dataKey;
    }
    public set dataKey(v: string) {
        this._dataKey = v;
        this.setAttribute("datakey", v);
    }

    /**
     * DataKey kořenového příspěvku
     */
    public get rootParentDataKey(): string {
        return this._rootParentDataKey;
    }
    public set rootParentDataKey(v: string) {
        this._rootParentDataKey = v;
    }

    /**
     * DataKey rodičovského příspěvku
     */
    public get parentDataKey(): string {
        return this._parentDataKey;
    }
    public set parentDataKey(v: string) {
        this._parentDataKey = v;
    }

    public set isSelected(v: boolean) {
        if (this._isSelected == v)
            return;

        this._isSelected = v;
        this.classList.toggle("selected", v);
        if (v)
            this.callEvent("selectedRecordRequest");
    }

    public get state(): CollapseState {
        return this._state;
    }

    public get childrenContainer(): HTMLUListElement {
        return this._childrenContainer;
    }

    public set childrenContainer(v: HTMLUListElement) {
        this._childrenContainer = v;
    }

    public isDeleted: boolean;
    public frameContext: FrameContext | undefined = undefined;
    public readonly isContainer: boolean = true;
    public isValid: boolean = true;
    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this.frameContext, cancellationToken);
    }
    //#endregion Properties

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    public addCells(_columns: Column[]): void {
        // Prázdná metoda
    }

    protected _constructor(): void {
        GenericControl._constructor(this as HTMLElement);
        //this.callEvent("childDataRequest", { promiseResolvedFnc: this._isDataLoadedDefferred.resolve }, true);
        this.classList.add(this._cssClass);
    }

    protected _initializeComponents(rowData: ICommentRowData): void {
        this.rootParentDataKey = rowData.data.hierarchy;
        this.parentDataKey = rowData.data.hierarchy;
        // kontejner pro hlavičku řádku (Jméno příjmení, datum. tři tečky s akcemi)
        this._rowHeaderContainer = createElement({ classes: this._cssClassHeader }) as HTMLDivElement;
        // prostřední kontejner samotný komentář
        this._rowCommentContainer = createElement({ classes: this._cssClassMid }) as HTMLDivElement;
        // kontejner pro přílohy
        this._rowAttachmentContainer = createElement({ classes: this._cssClassAttachmentsContainer }) as HTMLDivElement;
        // div zápatí řádku (tlačítka pro odpověď a skrytí odpovědí)
        this._rowFooterContainer = createElement({ classes: this._cssClassFooter }) as HTMLDivElement;

        if (this.hasNewRights) {
            this._rowFooterReply = createElement({ type: "button", classes: ["btn", this._cssClassFooter + "__button", "actions", "flat", "icon", "no-caption"] }) as HTMLButtonElement;
            Localization.addLocData(this._rowFooterReply, "GUI-687811"); //odpovědět
            this._rowFooterContainer.appendChild(this._rowFooterReply);
            this._rowFooterReply.addEventListener("click", this._eventListFnc["replyButtonClicked"] = () => {
                this.replyButtonClicked.trigger(this, null);
            });
        }

        if (rowData.hasChildren && !rowData.hasParent)
            this._addHideRepliesButton();

        const elementContainer = createElement({ classes: this._cssClassElementContainer }) as HTMLDivElement;
        // avatar pro řádek komentáře
        this._avatar = new Avatar();
        this._avatar.avatarName = rowData.data.author;
        elementContainer.appendChild(this._avatar);
        // kontejner hlavičky do root elementu
        elementContainer.appendChild(this._rowHeaderContainer);
        // konteiner s komentářem do root elementu
        elementContainer.appendChild(this._rowCommentContainer);
        // kontejner přílohy do root elementu
        elementContainer.appendChild(this._rowAttachmentContainer);
        // kontejner patičky do root elementu
        elementContainer.appendChild(this._rowFooterContainer);
        this._childrenContainer = createElement({ type: "ul", classes: this._cssClass + "__children" }) as HTMLUListElement;
        this.appendChild(elementContainer);
        this.appendChild(this._childrenContainer);
    }

    // vrati parentdatakey pokud je, jinak datakey
    public getParentDataKeyOrDataKey(): string {
        if (this.rootParentDataKey && this.rootParentDataKey != "0")
            return this.rootParentDataKey;
        return this.dataKey;
    }

    //#region Methods
    /**
     * Ziniciuje nový řádek
     * @param rowData Data k zobrazení v řádku
     * @param ownerGrid DataGrid, ke kterému řádek patří
     */
    public init(rowData: ICommentRowData, _ownerGrid: IDataGrid<IDataRowElement>): void {
        this._initializeComponents(rowData);

        this._dataCells = new Array<TableCell>();
        this.dataKey = rowData.data.id;

        if (this._dataKey == undefined)
            throw Error("DataRow has invalid dataKey");

        //jmeno
        const authorLabel: HTMLLabelElement = createElement({ type: "label", classes: this._cssClassHeader + "__author" }) as HTMLLabelElement;
        authorLabel.innerText = rowData.data.author;
        this._rowHeaderContainer.appendChild(authorLabel);

        //datum
        const dateLabel: HTMLLabelElement = createElement({ type: "label", classes: this._cssClassHeader + "__date" }) as HTMLLabelElement;
        const commentDate = new Date(rowData.data.dateCreate);

        const curCult = CultureInfo.getCultureInfo();
        const opt: Intl.DateTimeFormatOptions = { month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", second: undefined };

        dateLabel.innerText = commentDate.toLocaleString(curCult.name, opt);
        this._rowHeaderContainer.appendChild(dateLabel);

        // komentář
        const textLabel: HTMLLabelElement = createElement({ type: "label", classes: this._cssClassMid + "__comment" }) as HTMLLabelElement;
        textLabel.innerText = rowData.data.text;
        this._rowCommentContainer.appendChild(textLabel);

        // zobrazeni tri tecek se bude resit pozdeji
        //this._rowHeaderContainer.appendChild(this._rowHeaderActions);

        // kontejner patičky
        // přílohy
        for (const attachment of rowData.attachments) {
            const attachmentButton: HTMLButtonElement = createElement({ type: "button", classes: ["btn", this._cssClassAttachmentsContainer + "__attachment"] }) as HTMLButtonElement;
            const attachmentIconLabel: HTMLLabelElement = createElement({ type: "label", classes: ["icon", "ui-cloud-download", this._cssClassAttachmentsContainer + "__icon"] }) as HTMLLabelElement;
            const fileName = attachment.attachmentAttribute.split(":").pop();
            attachmentButton.innerText = fileName;
            attachmentButton.appendChild(attachmentIconLabel);
            this._rowAttachmentContainer.appendChild(attachmentButton);

            attachmentButton.addEventListener("click", () => {
                this._attachmentButtonClick(attachment.id + "", attachmentButton, rowData.attachmentClass, rowData.attachmentAttribute);
            });
        }
    }

    private async _addHideRepliesButton(): Promise<void> {
        this._hideRepliesButton = createElement({
            type: "button",
            classes: ["btn", this._cssClassFooter + "__next-button", "actions", "flat", "no-caption"],
        }) as HTMLButtonElement;
        this._rowFooterContainer.appendChild(this._hideRepliesButton);
        const chevronTop = createElement({ type: "span", classes: ["icon", "ui-chevron-top-small"] }) as HTMLButtonElement;
        const chevronBottom = createElement({ type: "span", classes: ["icon", "ui-chevron-bottom-small"] }) as HTMLButtonElement;
        const hideRepliesText = createElement({ type: "span" }) as HTMLButtonElement;
        const showRepliesText = createElement({ type: "span" }) as HTMLButtonElement;
        Localization.addLocData(hideRepliesText, "GUI-691127"); //skrýt odpovědi
        Localization.addLocData(showRepliesText, "GUI-691128"); //zobrazit odpovědi

        this._hideRepliesButton.appendChild(hideRepliesText);
        this._hideRepliesButton.appendChild(showRepliesText);
        this._hideRepliesButton.appendChild(chevronTop);
        this._hideRepliesButton.appendChild(chevronBottom);

        chevronBottom.hidden = showRepliesText.hidden = true;

        this._hideRepliesButton.addEventListener("click", this._eventListFnc["hideRepliesButtonClicked"] = () => {
            showRepliesText.hidden = chevronBottom.hidden = this._childrenContainer.hidden;
            hideRepliesText.hidden = chevronTop.hidden = !this._childrenContainer.hidden;
            this._childrenContainer.hidden = !this._childrenContainer.hidden;
        });

        this.replyButtonClicked.add(() => {
            if (this.childrenContainer.hidden)
                this._hideRepliesButton.click();
        });
    }

    public render(): HTMLElement {
        return this as HTMLElement;
    }

    public select(): void {
        // Prázdná metoda
    }

    public async selectWithCheckChanges(): Promise<void> {
        // Prázdná metoda
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    public setData(_attrName: string, _value: IAttrValue): Promise<void> {
        throw new Error("Not implemented");
    }

    private async _attachmentButtonClick(documentId: string, button: HTMLButtonElement, attachmentClassName: string, attachmentAttributeName: string): Promise<void> {
        const fileEditAdapter: IFileEdit = {
            getFileName: () => button.innerText,
            getArchiveDocumentId: () => this._archiveDocumentId,
            getLocalFile: () => this._file,
            getSignatureProviderProfile: () => this._signatureProviderProfile,
            getDigitalSignatureState: () => this._digitalSignatureState,
            getControlValue: () => null,
            setSignatureProviderProfile: (value) => this._setSignatureProviderProfile = value,
            setSigningInProgress: (value: boolean) => this._signingInProgress = value,
            updateCanSign: (_param: any) => undefined,
            setValid: () => { /* Prázdná arrow funkce */ },
            setInvalid: () => { /* Prázdná arrow funkce */ },
            setStatus: (status: FileEditStatus) => this._setStatus(status),
            changeProgressStatus: (_type?: FileEditProgressStatus, _value?: number) => undefined,
            fileEditElement: null,
        };
        const fileCore: FileEditCore = new FileEditCore(logger, fileEditAdapter, attachmentAttributeName, this);
        const actualObject = await OWManager.getObject(documentId, attachmentClassName, undefined, undefined, false, undefined);
        fileCore.actualObject = actualObject;

        fileCore.downloadFile(false);
    }

    public dispose(): void {
        this.childrenRows.forEach((row) => {
            row.dispose();
        });
        if (this._eventListFnc["hideRepliesButtonClicked"])
            this._hideRepliesButton.removeEventListener("click", this._eventListFnc["hideRepliesButtonClicked"]);
    }

    public override remove(): void {
        this.childrenRows.forEach((row) => {
            row.remove();
        });
        super.remove();
    }

    /**
    * Připojí element na začátek vlákna
    * @param elementToPlace element k umístění
    * @param checkRepliesButton zkontrolovat, zda je zobrazene tlačítko Skrýt/Zobrazit odpovědi
    */
    public insertFirstChild(elementToPlace: HTMLElement, checkRepliesButton: boolean = true): void {
        if (checkRepliesButton && !this._hideRepliesButton)
            this._addHideRepliesButton();
        this.childrenContainer.insertAdjacentElement("afterbegin", elementToPlace);
    }

    /**
    * Připojí komentář nakonec vlákna
    * @param elementToPlace element k umístění
    * @param checkRepliesButton zkontrolovat, zda je zobrazene tlačítko Skrýt/Zobrazit odpovědi
    */
    public appendRow(elementToPlace: CommentaryFrameDataRow, checkRepliesButton: boolean = true): void {
        if (checkRepliesButton && !this._hideRepliesButton)
            this._addHideRepliesButton();
        this.childrenContainer.appendChild(elementToPlace);
    }
}

registerCustomElement("tsc-CommentaryFrameDataRow", CommentaryFrameDataRow);
